/**
 * Laden mit drei ehrlichen Zuständen: lädt, ging schief, ist da.
 *
 * Warum ein eigener kleiner Haken und keine Abfragebibliothek: Das Backoffice
 * hat wenige Bildschirme und wenige Nutzer. Was es braucht, ist ein Ladehinweis,
 * eine sichtbare Fehlermeldung mit „nochmal versuchen" und ein Weg, nach dem
 * Speichern neu zu laden. Das sind die vierzig Zeilen unten. Eine Bibliothek
 * mit Zwischenspeicher, Hintergrundauffrischung und eigenen Regeln wäre hier
 * mehr Ursache für Fehler als Hilfe.
 *
 * Wichtig ist der abgebrochene Fall: Wer schnell zwischen zwei Seiten klickt,
 * bekommt sonst die Antwort der alten Seite in die neue geschrieben. Deshalb
 * merkt sich jeder Lauf seine Nummer und schreibt nur, wenn er noch der
 * aktuelle ist.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { alsKlarfehler, type Klarfehler } from './fehler';
import { Speicherfehler } from './speichern';

export type Abfragestand<T> = {
  daten: T | null;
  laedt: boolean;
  fehler: Klarfehler | null;
  /**
   * Neu laden — nach dem Speichern oder wenn der Nutzer es verlangt. Das
   * Versprechen erfüllt sich, wenn der neue Stand (oder sein Fehler) da ist:
   * Wer bis dahin einen Knopf gesperrt hält, verhindert einen zweiten Klick
   * mit dem alten Stand (H1.3b). Es scheitert nie — der Fehler steht in
   * `fehler`.
   */
  auffrischen: () => Promise<void>;
};

export function useAbfrage<T>(
  holen: () => Promise<T>,
  abhaengigkeiten: unknown[] = [],
): Abfragestand<T> {
  const [daten, setDaten] = useState<T | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<Klarfehler | null>(null);
  const lauf = useRef(0);

  // Die Funktion wird bewusst nicht in die Abhängigkeiten genommen: sie wird
  // an der Aufrufstelle meist inline geschrieben und wäre bei jedem Rendern
  // neu — das ergäbe eine Endlosschleife.
  const holenRef = useRef(holen);
  holenRef.current = holen;

  const ausfuehren = useCallback((): Promise<void> => {
    const meine = ++lauf.current;
    setLaedt(true);
    setFehler(null);
    return holenRef
      .current()
      .then((ergebnis) => {
        if (meine !== lauf.current) return;
        setDaten(ergebnis);
        setLaedt(false);
      })
      .catch((problem) => {
        if (meine !== lauf.current) return;
        setFehler(
          problem instanceof Speicherfehler
            ? problem.klar
            : alsKlarfehler(problem),
        );
        setLaedt(false);
      });
  }, []);

  useEffect(() => {
    void ausfuehren();
  }, abhaengigkeiten);

  return { daten, laedt, fehler, auffrischen: ausfuehren };
}
